import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CardHeader from "./CardHeader";
import {
  faAddressCard,
  faPenToSquare,
} from "@fortawesome/free-solid-svg-icons";
import * as f from "../../../utils/formatter";
import { isGrantedAny, role } from "shared/src/roles.mjs";
import { useRoles } from "../../../context/AuthenticationTokenContext";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formData, FormErrorList, useFormAction } from "../../../form";
import ButtonLoader from "../../ButtonLoader";
import { diff, updateInIfExists } from "shared/src/util.mjs";
import * as api from "../../../services/api";
import { useRevalidator } from "react-router";

const permissions = {
  view: {
    phone: new Set([
      role.Floor_Manager,
      role.Back_Office,
      role.Front_Office,
      role.Dental_Assistant,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only,
      role.Read_Only_Full,
    ]),
    email: new Set([
      role.Floor_Manager,
      role.Back_Office,
      role.Front_Office,
      role.Dental_Assistant,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only,
      role.Read_Only_Full,
    ]),
    address: new Set([
      role.Back_Office,
      role.Front_Office,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only_Full,
    ]),
  },
  edit: {
    phone: new Set([
      role.Back_Office,
      role.Front_Office,
      role.Manager_Customer_Care,
    ]),
    email: new Set([
      role.Back_Office,
      role.Front_Office,
      role.Manager_Customer_Care,
    ]),
  },
};

function FieldForm({ client, field }) {
  const { t } = useTranslation();

  switch (field) {
    case "email": {
      return (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr" }}>
          <TextField
            label={t("entity.client.email")}
            name="pending_changes.email"
            defaultValue={getChangeableValue(client, "email").value ?? ""}
            slotProps={{ htmlInput: { type: "email" } }}
            type="email"
          />
        </Box>
      );
    }
    case "phone": {
      return (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 6fr", gap: 2 }}>
          <TextField
            label={t("entity.client.phone_prefix")}
            name="pending_changes.phone_prefix"
            defaultValue={
              getChangeableValue(client, "phone_prefix").value ?? ""
            }
          />
          <TextField
            label={t("entity.client.phone")}
            name="pending_changes.phone"
            defaultValue={getChangeableValue(client, "phone").value ?? ""}
          />
        </Box>
      );
    }
  }
}

function trimAndRemoveEmpty(obj) {
  const res = {};
  for (const k in obj) {
    const v = obj[k];
    if (typeof v === "string") {
      const s = v.trim();
      if (s.length > 0) {
        res[k] = s;
      } else {
        res[k] = null;
      }
    } else {
      res[k] = v;
    }
  }

  return res;
}

function formConverter(client, data) {
  return updateInIfExists(data, ["pending_changes"], (v) => {
    const formPendingChanges = trimAndRemoveEmpty({
      ...client.pending_changes,
      ...v,
    });

    const difference = diff(formPendingChanges, client);

    return difference[0] ?? {};
  });
}

function EditDialog({ close, field, client }) {
  const { t } = useTranslation();
  const revalidator = useRevalidator();
  const { loading, errors, onSubmit } = useFormAction(
    (e) => api.patchClient(formConverter(client, formData(e.target))),
    () => {
      revalidator.revalidate();
      close();
    },
  );

  return (
    <Dialog open={true} onClose={close}>
      <DialogTitle>Upravit kontaktní údaje</DialogTitle>
      <form onSubmit={onSubmit}>
        <DialogContent>
          <input type="hidden" name="id" value={client.id} />
          <FieldForm client={client} field={field} />
          <FormErrorList formErrors={errors} />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => close()}>
            {t("form.cancel")}
          </Button>
          <Button variant="contained" type="submit" disabled={loading}>
            {loading && <ButtonLoader />}
            {t("form.submit")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function Info({ label, value, edit }) {
  const { t } = useTranslation();

  return (
    <Typography variant="body1">
      <strong>{label}</strong>
      {edit != null && (
        <Tooltip title={t("form.edit", "Edit")}>
          <IconButton onClick={() => edit()} sx={{ color: "primary.main" }}>
            <FontAwesomeIcon icon={faPenToSquare} />
          </IconButton>
        </Tooltip>
      )}
      <br />
      {value}
    </Typography>
  );
}

/**
 * @param {object} m
 * @param {string} k
 */
function getChangeableValue(m, k) {
  if (Object.prototype.hasOwnProperty.call(m.pending_changes, k)) {
    return { pending: true, value: m.pending_changes[k], original: m[k] };
  }

  return { pending: false, value: m[k], original: m[k] };
}

function ChangeableInfo({ label, m, input, view, edit }) {
  const { t } = useTranslation();
  const data = input.reduce((acc, k) => {
    acc[k] = getChangeableValue(m, k);

    return acc;
  }, {});
  const viewData = view(data);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          height: "1.8rem",
        }}
      >
        <strong>{label}</strong>
        {edit != null && (
          <Tooltip title={t("form.edit", "Edit")}>
            <IconButton size="small" onClick={() => edit()} color="primary">
              <FontAwesomeIcon size="xs" icon={faPenToSquare} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {viewData.pending ? (
        <>
          <Typography variant="body1" component="span">
            {viewData.original}
          </Typography>
          <br />
          <Tooltip title={"Čeká na potvrzení změny"}>
            <Typography variant="body1" component="span" color="textDisabled">
              {viewData.value}
            </Typography>
          </Tooltip>
        </>
      ) : (
        <>{viewData.value}</>
      )}
    </Box>
  );
}

function phoneView(v) {
  const { phone, phone_prefix } = v;

  return {
    value: f.value(
      f.phone({ phone_prefix: phone_prefix.value, phone: phone.value }),
    ),
    original: f.value(
      f.phone({
        phone_prefix: phone_prefix.original,
        phone: phone.original,
      }),
    ),
    pending: Object.values(v).some((p) => p.pending),
  };
}

function oneView(v) {
  return Object.values(v)[0];
}

function ContactInfo({ client }) {
  const roles = useRoles();
  const [edit, setEdit] = React.useState(null);

  return (
    <>
      <Stack spacing={5} direction="row" sx={{ mb: 3 }}>
        <Stack spacing={3} direction="column">
          {isGrantedAny(roles, permissions.view.email) && (
            <ChangeableInfo
              label="Email"
              m={client}
              input={["email"]}
              view={oneView}
              edit={
                isGrantedAny(roles, permissions.edit.email)
                  ? () => setEdit("email")
                  : null
              }
            />
          )}
          {isGrantedAny(roles, permissions.view.phone) && (
            <ChangeableInfo
              label="Telefon"
              m={client}
              input={["phone_prefix", "phone"]}
              view={phoneView}
              edit={
                isGrantedAny(roles, permissions.edit.phone)
                  ? () => setEdit("phone")
                  : null
              }
            />
          )}
        </Stack>

        {isGrantedAny(roles, permissions.view.address) && (
          <Stack spacing={3} direction="column">
            <Info label="Adresa trvalého bydliště" value={f.address(client)} />
            <Info
              label="Korespondenční adresa"
              value={f.correspondenceAddress(client)}
            />
          </Stack>
        )}
      </Stack>
      {edit != null && (
        <EditDialog client={client} close={() => setEdit(null)} field={edit} />
      )}
    </>
  );
}

export function ContactCard({ client }) {
  const theme = useTheme();

  return (
    <Card>
      <CardContent>
        <CardHeader
          title="Kontakt"
          color={theme.palette.info.main}
          faIcon={faAddressCard}
        />
        <ContactInfo client={client} />
      </CardContent>
    </Card>
  );
}
